import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCheckCircle, faCircle } from '@fortawesome/free-solid-svg-icons';
import authService from '../../../services/authService';
import { PASSWORD_REUSE_ERROR_MESSAGE } from '../../../utils/passwordValidation';
import FormActionButtons from '../../Common/FormActionButtons/FormActionButtons';
import './HeaderScreens.css';

const initialState = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};

const ChangePasswordScreen = ({ onClose }) => {
  const [formValues, setFormValues] = useState(initialState);
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  const [visibleFields, setVisibleFields] = useState({
    current: false,
    newValue: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [touched, setTouched] = useState({});

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    if (name === 'currentPassword' && !formValues.currentPassword) {
      setErrors((prev) => ({ ...prev, currentPassword: 'Current password is required.' }));
    }
  };

  const getLiveValidationErrors = (values) => {
    const liveErrors = {};
    const currentTrimmed = (values.currentPassword || '').trim();
    const newTrimmed = (values.newPassword || '').trim();

    if (values.newPassword) {
      const passwordError = validatePassword(values.newPassword);
      if (passwordError) {
        liveErrors.newPassword = passwordError;
      }
    }

    if (currentTrimmed && newTrimmed && currentTrimmed === newTrimmed) {
      liveErrors.newPassword = 'New password must be different from current password.';
    }

    if (values.confirmPassword && values.newPassword !== values.confirmPassword) {
      liveErrors.confirmPassword = 'Passwords do not match.';
    }

    return liveErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const nextValues = { ...formValues, [name]: value };
    setFormValues(nextValues);

    const liveErrors = getLiveValidationErrors(nextValues);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.currentPassword;
      delete next.newPassword;
      delete next.confirmPassword;
      delete next.submit;
      if (!nextValues.currentPassword && touched.currentPassword) {
        next.currentPassword = 'Current password is required.';
      }
      return { ...next, ...liveErrors };
    });

    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const toggleVisibility = (field) => {
    setVisibleFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }
    // Check for uppercase, lowercase, number, and special character
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      return 'Password must contain uppercase, lowercase, numbers, and special characters.';
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitDisabled || isSubmitting) {
      return; // Button is visually enabled, but not clickable when invalid
    }

    setErrors({});
    setSuccessMessage('');

    // Validation
    const newErrors = {};

    if (!formValues.currentPassword) {
      newErrors.currentPassword = 'Current password is required.';
    }

    if (!formValues.newPassword) {
      newErrors.newPassword = 'New password is required.';
    } else {
      const passwordError = validatePassword(formValues.newPassword);
      if (passwordError) {
        newErrors.newPassword = passwordError;
      }
    }

    if (!formValues.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password.';
    } else if (formValues.newPassword !== formValues.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }

    if (formValues.currentPassword === formValues.newPassword) {
      newErrors.newPassword = PASSWORD_REUSE_ERROR_MESSAGE;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authService.changePassword(
        formValues.currentPassword,
        formValues.newPassword
      );

      if (response.data && response.data.success) {
        setSuccessMessage(response.data.message || 'Password changed successfully!');
        setFormValues(initialState);
        
        // Close after 2 seconds
        setTimeout(() => {
          onClose?.();
        }, 2000);
      } else {
        const message = response.data?.message || 'Failed to change password. Please try again.';
        if (message === PASSWORD_REUSE_ERROR_MESSAGE) {
          setErrors({ newPassword: message });
        } else {
          setErrors({ submit: message });
        }
      }
    } catch (error) {
      console.error('Change password error:', error);
      const errorMessage = error.response?.data?.message ||
                          error.message ||
                          'An error occurred while changing password. Please try again.';
      if (errorMessage === PASSWORD_REUSE_ERROR_MESSAGE) {
        setErrors({ newPassword: errorMessage });
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormValues(initialState);
    setErrors({});
    setSuccessMessage('');
    onClose?.();
  };

  const hasSameAsCurrentError =
    (formValues.currentPassword || '').trim() &&
    (formValues.newPassword || '').trim() &&
    (formValues.currentPassword || '').trim() === (formValues.newPassword || '').trim();

  const isSubmitDisabled =
    isSubmitting ||
    hasSameAsCurrentError ||
    Boolean(errors.newPassword) ||
    Boolean(errors.confirmPassword) ||
    !formValues.currentPassword ||
    !formValues.newPassword ||
    !formValues.confirmPassword;

  return (
    <div
      className="header-screen-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="header-screen-card change-password-card"
      >
        <div className="header-screen-card-header">
          <div>
            <h2>Change password</h2>
            <p className="header-screen-subtitle">
              Keep your account secure by updating your password regularly.
            </p>
          </div>
          <button
            type="button"
            className="header-screen-close"
            aria-label="Close change password panel"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <form className="change-password-form" onSubmit={handleSubmit}>
          {errors.submit && (
            <div className="alert alert-danger" style={{ marginBottom: '20px', padding: '12px', borderRadius: '4px', backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' }}>
              {errors.submit}
            </div>
          )}

          {successMessage && (
            <div className="alert alert-success" style={{ marginBottom: '20px', padding: '12px', borderRadius: '4px', backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb' }}>
              {successMessage}
            </div>
          )}

          <div className="form-field">
            <label htmlFor="currentPassword">Current Password</label>
            <div className="input-with-icon">
              <input
                id="currentPassword"
                name="currentPassword"
                type={visibleFields.current ? 'text' : 'password'}
                value={formValues.currentPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter your current password"
                required
                className={errors.currentPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="icon-button"
                onClick={() => toggleVisibility('current')}
                aria-label="Toggle current password visibility"
              >
                <FontAwesomeIcon icon={visibleFields.current ? faEyeSlash : faEye} />
              </button>
            </div>
            {errors.currentPassword && (
              <span className="cp-field-error">
                {errors.currentPassword}
              </span>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="newPassword">
              New Password <span className="required">*</span>
              <span className="info-icon" title="Use at least 8 characters with numbers and symbols">
                <i className="fa fa-info-circle" aria-hidden="true" />
              </span>
            </label>
            <div className="input-with-icon" style={{ position: 'relative' }}>
              <input
                id="newPassword"
                name="newPassword"
                type={visibleFields.newValue ? 'text' : 'password'}
                value={formValues.newPassword}
                onChange={handleChange}
                onFocus={() => setShowPasswordCriteria(true)}
                onBlur={() => setShowPasswordCriteria(false)}
                placeholder="Create a strong password"
                minLength={8}
                required
                className={errors.newPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="icon-button"
                onClick={() => toggleVisibility('newValue')}
                aria-label="Toggle new password visibility"
              >
                <FontAwesomeIcon icon={visibleFields.newValue ? faEyeSlash : faEye} />
              </button>
              {showPasswordCriteria && (
                <div className="ap-password-criteria-block">
                  <div className={`ap-criteria-item ${(formValues.newPassword || '').length >= 8 ? 'fulfilled' : ''}`}>
                    <FontAwesomeIcon icon={(formValues.newPassword || '').length >= 8 ? faCheckCircle : faCircle} className="ap-criteria-icon" /> Minimum 8 characters
                  </div>
                  <div className={`ap-criteria-item ${/[A-Z]/.test(formValues.newPassword || '') ? 'fulfilled' : ''}`}>
                    <FontAwesomeIcon icon={/[A-Z]/.test(formValues.newPassword || '') ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Uppercase letter
                  </div>
                  <div className={`ap-criteria-item ${/[a-z]/.test(formValues.newPassword || '') ? 'fulfilled' : ''}`}>
                    <FontAwesomeIcon icon={/[a-z]/.test(formValues.newPassword || '') ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Lowercase letter
                  </div>
                  <div className={`ap-criteria-item ${/[0-9]/.test(formValues.newPassword || '') ? 'fulfilled' : ''}`}>
                    <FontAwesomeIcon icon={/[0-9]/.test(formValues.newPassword || '') ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Number
                  </div>
                  <div className={`ap-criteria-item ${/[!@#$%^&*(),.?":{}|<>]/.test(formValues.newPassword || '') ? 'fulfilled' : ''}`}>
                    <FontAwesomeIcon icon={/[!@#$%^&*(),.?":{}|<>]/.test(formValues.newPassword || '') ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Special character
                  </div>
                </div>
              )}
            </div>
            {!showPasswordCriteria && errors.newPassword && (
              <span className="cp-field-error">
                {errors.newPassword}
              </span>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="input-with-icon">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={visibleFields.confirm ? 'text' : 'password'}
                value={formValues.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter the new password"
                required
                className={errors.confirmPassword ? 'error' : ''}
              />
              <button
                type="button"
                className="icon-button"
                onClick={() => toggleVisibility('confirm')}
                aria-label="Toggle confirm password visibility"
              >
                <FontAwesomeIcon icon={visibleFields.confirm ? faEyeSlash : faEye} />
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="cp-field-error">
                {errors.confirmPassword}
              </span>
            )}
          </div>

          <FormActionButtons
            onCancel={handleCancel}
            disabled={isSubmitting}
            cancelDisabled={isSubmitting}
            isSubmitting={isSubmitting}
            addLabel="Change Password"
            submittingLabel="Changing..."
            submitStyle={{
              cursor: isSubmitDisabled ? 'not-allowed' : 'pointer'
            }}
          />
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordScreen;

